import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import gql from 'graphql-tag';
import { withRouter, WithRouterProps } from 'react-router';
import { Location } from 'history';

import { RCListingCard, RCNudgeProps, RCPriceBlockProps } from '@reverbdotcom/cadence/components';
import { ThemeType } from '@reverbdotcom/cadence/components/types';

import ConditionDisplay from './condition_display';
import PriceDisplay from './price_display';
import PriceWithShipping from './price_with_shipping';
import ImageCarousel from './image_carousel';
import ListingCardNudge from './listing_card_nudge';
import {
  ListingCardFields,
  FinancingFields,
  InOtherCartsCardData,
  core_apimessages_Address as Address,
} from '../rql';
import AddToCartButton from './add_to_cart_button';
import { WatchBadgeFragment, WatchBadge, DisplayStyle } from './watch_badge';
import { WatchParentComponentName } from '../event_tracking/watchlist';
import CoreLink from './core_link';
import { isMarketplacePage, isUsingProximityFeatures, ITEM_WITH_SLUG_ROUTE, listingItemPagePath } from '../url_helpers';
import { useUser } from '../user_hooks';
import { IUser } from './user_context_provider';
import { useTheme } from './theme_context_provider';
import BumpTag, { shouldShowBumpTag } from './bump_tag';
import { buildListingClickEvent } from '../elog/mparticle_tracker';
import { LISTING_STATES, WEB } from '../constants';
import experiments from '../experiments';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';
import { ListingOfferMetadata } from './listing_offer_metadata';
import ListingShopCountryNudge from './listing_shop_country_nudge';
import ListingCardSignals, { ListingWithSignals } from './listing_card_signals';
import { isGreatValue } from '../listing_great_value';
import { isNewListing } from '../newListingNudge';

import {
  getNudges,
  getEyebrow,
  getPriceNudgeProps,
  buildListingForBuyer,
  ListingForBuyerFragment,
  getA11yPriceDescription,
} from '../listing_card_helpers';

const DISPLAY_STYLE = 'squareCard';

type ListingCardListingFields = ListingCardFields.Fragment & FinancingFields.Fragment & InOtherCartsCardData.Fragment & ListingWithSignals;
interface IExternalProps {
  // This type is likely _more_ than what this component needs.
  // Cutting a fragment for this component would help better communicate
  // the contract here.
  listing?: ListingCardListingFields;
  position?: number;
  soldListing?: boolean;
  footerContent?: React.ReactNode;
  hideMetadataAsOffer?: boolean;
  trackingName?: string;
  displayAddToCart?: boolean;
  listingsCount?: number;
  combinedShippingDiscount?: boolean;
  trackingQuery?: string;
  showFlag?: boolean;
  // please use a WatchBadgeParentComponent value when possible instead of a plain string!
  watchBadgeParentComponentName?: WatchParentComponentName | string;
  displayGreatValue?: boolean;
  useRCListingCard?: boolean;
}

type IProps = IExternalProps & WithRouterProps;

export function ListingCard(props: IProps) {
  const user = useUser();
  const { theme } = useTheme();
  const webTwoTheme = theme === ThemeType.web2;
  const showNewListingCard = webTwoTheme || !!props.useRCListingCard;

  const {
    combinedShippingDiscount,
    displayAddToCart,
    displayGreatValue = true,
    footerContent,
    hideMetadataAsOffer,
    listing,
    listingsCount,
    location,
    position,
    showFlag,
    soldListing,
    trackingName,
    trackingQuery,
    watchBadgeParentComponentName,
  } = props;

  if (!listing) {
    if (showNewListingCard) {
      return <RCListingCard.Skeleton />;
    }

    return <PlaceholderCard />;
  }

  const decoratedListing = buildListingForBuyer(listing, combinedShippingDiscount);
  const isBumped = shouldShowBumpTag(listing);
  const hasDiscount = !!listing.pricing.originalPrice;
  const ribbonText = listing?.pricing?.ribbon?.display || null;

  function listingUrl() {
    if (soldListing) {
      return (
        ITEM_WITH_SLUG_ROUTE.expand({
          slug: listing.slug,
          id: listing.id,
          show_sold: true,
        })
      );
    }

    return (
      listingItemPagePath(listing)
    );
  }

  function renderOriginalPrice() {
    if (!hasDiscount) { return null; }

    return (
      <div className="grid-card__original-price">
        <span className="mr-space td-line-through">
          {listing.pricing.originalPrice.display}
        </span>
        <span className="grid-card__discount">
          {ribbonText}
        </span>
      </div>
    );
  }

  function renderMetadata() {
    if (hideMetadataAsOffer) return null;

    return (
      <ListingOfferMetadata listing={listing} />
    );
  }

  function clickEvent() {
    const sellerCountryCode = listing.shop.address?.country?.countryCode;

    return {
      experiments: formatExperimentsForEventAttributes(
        user,
        [
          experiments.LISTING_CARD_LOCATION,
          experiments.DUMMY,
          experiments.DATA_DRIVEN_WELL_PRICED_LISTINGS,
        ],
      ),
      query: trackingQuery,
      shownAsBumped: isBumped,
      ...buildListingClickEvent({
        listing: listing,
        componentName: trackingName,
        displayStyle: DISPLAY_STYLE,
        position,
        listingsCount,
        city: listing.shop.address?.locality,
        region: listing.shop.address?.region,
        countryCode: sellerCountryCode,
        isListingDomesticToBuyer: isListingDomesticToBuyer(sellerCountryCode, user?.countryCode),
      }),
    };
  }

  function renderProximityShopLocation() {
    const shopAddress = listing.shop?.address;
    if (!shopAddress) return null;

    if (showProximityContent(shopAddress, user, location)) {
      return (
        <div data-proximity-location className="size-80 color-secondary">
          {I18n.t('commons.listingCard.location.sameCountryUs', {
            city: shopAddress.locality, state: shopAddress.region,
          })}
        </div>
      );
    }

    return null;
  }

  function renderShopLocationCountryFlag() {
    const shopAddress = listing.shop?.address;
    if (!shopAddress || !shopAddress.country) return null;

    if (showFlag || showFlagForMarketplace(user, location)) {
      return (
        <div>
          <ListingShopCountryNudge
            data-shop-country-nudge
            country={shopAddress.country}
          />
        </div>
      );
    }

    return null;
  }

  function shopLocation() {
    const shopAddress = listing.shop?.address;
    if (!shopAddress) return '';

    if (showProximityContent(shopAddress, user, location)) {
      return I18n.t('commons.listingCard.location.sameCountryUs', {
        city: shopAddress.locality, state: shopAddress.region,
      });
    }

    return '';
  }

  function showLocalPickupNudge() {
    const { shop } = listing;
    const localPickupAvailable = decoratedListing.localPickupAvailable && !decoratedListing.localPickupOnly;

    return localPickupAvailable && showProximityContent(shop?.address, user, location);
  }

  function getRCListingCardNudges(): RCNudgeProps[] {
    const shopAddress = listing.shop?.address;
    const includeFlagNudge = showFlag || showFlagForMarketplace(user, location);

    const config = {
      displayMultiple: true,
      greatValue: displayGreatValue && isGreatValue(listing, user),
      inOtherCartsCount: listing?.otherBuyersWithListingInCartCounts,
      localPickupOnly: decoratedListing.localPickupOnly,
      localPickupAvailable: showLocalPickupNudge(),
      returnPolicyDays: decoratedListing?.returnPolicyDays,
      newListing: isNewListing(listing) && isMarketplacePage(location),
      country: includeFlagNudge ? shopAddress.country : undefined,
    };

    return getNudges(config);
  }

  function renderNudges() {
    // Signal system currently not in use due to performance concerns
    if (listing.signals) {
      return <ListingCardSignals listing={listing} />;
    }

    return (
      <ListingCardNudge
        freeExpeditedShipping={decoratedListing.freeExpeditedShippingAvailable}
        freeShipping={decoratedListing.freeStandardShippingAvailable}
        greatValue={displayGreatValue && isGreatValue(listing, user)}
        inOtherCartsCount={listing?.otherBuyersWithListingInCartCounts}
        localPickupAvailable={showLocalPickupNudge()}
        localPickupOnly={decoratedListing.localPickupOnly}
        returnPolicyDays={decoratedListing.returnPolicyDays}
        newListing={isNewListing(listing) && isMarketplacePage(location)}
        displayMultiple
      />
    );
  }

  function getPricePropForRCCard(): RCPriceBlockProps {
    const showDiscountPrice = !combinedShippingDiscount && decoratedListing.isLive;

    return {
      priceDisplay: decoratedListing.primaryPriceDisplay,
      discountPriceDescription: decoratedListing.discountPriceDescription,
      priceNudge: getPriceNudgeProps(decoratedListing.priceNudge),
      shippingPromo: decoratedListing.shippingPromo,
      a11yPriceDescription: getA11yPriceDescription(decoratedListing),
      discountPriceDisplay: showDiscountPrice ? decoratedListing.discountedPriceDisplay : undefined,
      shippingDisplay: combinedShippingDiscount ? decoratedListing.primaryShippingDisplay : undefined,
      shippingStrike: !!combinedShippingDiscount,
    };
  }

  const openNewTab = user && user.deviceName === WEB;
  const images = listing.images.map(i => (i.source));
  const violatorText = listing.state !== LISTING_STATES.LIVE ? listing.stateDescription : null;

  if (showNewListingCard) {
    return (
      <>
        <RCListingCard
          linkElement={(children) => (
            <CoreLink
              to={listingUrl()}
              target={openNewTab ? '_blank' : undefined}
              clickEvent={clickEvent()}
            >
              {children}
            </CoreLink>
          )}
          thumbnailElement={(<ImageCarousel images={images}/>)}
          title={listing.title}
          conditionLabel={decoratedListing.conditionLabel}
          shopLocation={shopLocation()}
          price={getPricePropForRCCard()}
          nudges={getRCListingCardNudges()}
          fullHeight
          eyebrow={getEyebrow(listing)}
          favoriteButtonElement={(
            <WatchBadge
              listing={listing}
              displayStyle={DisplayStyle.RC_FAVORITE_BUTTON}
              parentComponentName={watchBadgeParentComponentName || trackingName}
              useToast
            />
          )}
          violatorText={violatorText}
        />
        {footerContent}
      </>
    );
  }

  const classes = classNames(
    'grid-card',
    { 'grid-card--software': decoratedListing.isDigital },
    { 'grid-card--discounted': hasDiscount },
    { 'grid-card--with-cart-action': displayAddToCart },
  );

  return (
    <div className={classes}>
      <CoreLink
        className="grid-card__inner"
        to={listingUrl()}
        target={openNewTab ? '_blank' : undefined}
        clickEvent={clickEvent()}
      >
        {renderMetadata()}
        <div className="grid-card__main">
          <div className="grid-card__image">
            <ImageCarousel
              images={images}
            />
            {isBumped &&
              <div className="grid-card__bump-tag">
                <BumpTag />
              </div>
            }
            {listing.state !== 'live' &&
              <div className="grid-card__sold">
                {listing.stateDescription}
              </div>
            }
          </div>
          <div className="grid-card__main__text">
            <h4 className="grid-card__title">
              {listing.title}
            </h4>
            {renderProximityShopLocation()}
          </div>
        </div>
        <div className="grid-card__footer">
          {renderOriginalPrice()}
          <div
            className="grid-card__footer__pricing"
          >
            <div className="grid-card__price">
              <ListingPrice
                combinedShippingDiscount={combinedShippingDiscount}
                listing={listing}
              />
            </div>
            <ListingCondition listing={listing} />
          </div>
          {renderShopLocationCountryFlag()}
          {renderNudges()}
          {footerContent}
        </div>
      </CoreLink>
      {displayAddToCart &&
        <AddToCartButton
          listing={listing}
          position={position}
          parentComponentName={trackingName}
        />
      }
      <div className="grid-card__watch">
        <WatchBadge
          listing={listing}
          displayStyle={DisplayStyle.ICON}
          parentComponentName={watchBadgeParentComponentName || trackingName}
          useToast
        />
      </div>
    </div>
  );
}

function isListingDomesticToBuyer(sellerCountryCode?: string, buyerCountryCode?: string): boolean {
  if (!(sellerCountryCode && buyerCountryCode)) { return false; }
  return sellerCountryCode === buyerCountryCode;
}

function showFlagForMarketplace(user: Partial<IUser>, location: Location) {
  return user.countryCode !== 'US' && optionalPathnameIncludes(location, '/marketplace');
}

function optionalPathnameIncludes(location: Location, path: string): boolean {
  if (!location?.pathname) {
    return false;
  }

  return location?.pathname?.includes(path);
}

function showProximityContent(shopAddress: Address, user: Partial<IUser>, location: Location) {
  return isUsingProximityFeatures(location) &&
    shopAddress?.country?.countryCode === 'US' &&
    user.countryCode === 'US';
}

function ListingPrice(props: {
  listing: ListingCardListingFields,
  combinedShippingDiscount: boolean,
}) {
  const { listing, combinedShippingDiscount } = props;
  const decoratedListing = buildListingForBuyer(listing);

  if (combinedShippingDiscount) {
    return (
      <PriceWithShipping
        listing={listing}
        showCombinedRate
        useCombinedShippingStrikethrough
        hideOriginalPrice
      />
    );
  }

  return (
    <PriceDisplay
      amountCents={listing.pricing.buyerPrice.amountCents}
      currency={listing.pricing.buyerPrice.currency}
      display={listing.pricing.buyerPrice.display}
      auction={decoratedListing.isAuction}
    />
  );
}

function ListingCondition(props: { listing: ListingCardListingFields }) {
  const { listing } = props;
  const decoratedListing = buildListingForBuyer(listing);

  if (decoratedListing.isDigital) return null;
  if (!listing.condition) return null;

  return (
    <div className="grid-card__condition">
      <ConditionDisplay
        slug={listing.condition.conditionSlug}
        displayName={listing.condition.displayName}
        textOnly
      />
    </div>
  );
}

export function PlaceholderCard() {
  return (
    <div className="grid-card grid-card--placeholder">
      <div className="grid-card__inner">
        <div className="grid-card__main">
          <div className="grid-card__image" />
          <div className="grid-card__main__text">
            <div className="grid-card__title" />
          </div>
        </div>

        <div className="grid-card__footer">
          <div className="grid-card__footer__pricing">
            <div className="grid-card__price" />
          </div>
        </div>
      </div>
    </div>
  );
}

export const ListingCardFragment = gql`
  fragment ListingCardFields on Listing {
    _id
    ...ListingForBuyerFields
    ...WatchBadgeData
  }
  ${ListingForBuyerFragment}
  ${WatchBadgeFragment}
`;

export default withRouter(ListingCard);
